import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api, { getStoredSessionToken, setStoredSessionToken } from "./api";
import { toast } from "sonner";
import { configureIAP, identifyIAP, resetIAP } from "./iap";
import { isIOS } from "./platform";
import { getTtsVoice, setTtsVoice, subscribeTtsVoiceChange, clearLocalTtsVoice } from "./ttsVoice";

// Single module-level latch so we never register the deep-link listener twice
// (StrictMode / hot reload). Also lets us assert: once the listener fires, we
// won't drop the event because of a stale closure.
let _deepLinkListenerInstalled = false;

const AuthContext = createContext({
  user: null,
  usage: null,
  loading: true,
  signIn: () => {},
  signInWithApple: async () => {},
  signOut: async () => {},
  refresh: async () => {},
  startTrial: async () => {},
  openPaywall: () => {},
  isProEffective: false,
});

export function AuthProvider({ children, openPaywall }) {
  const [user, setUser] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const prevRemainingRef = useRef({});

  const KIND_LABEL = {
    translate_text: "text translations",
    translate_voice: "voice translations",
    receipt: "receipt scans",
    menu: "menu scans",
  };

  const refresh = useCallback(async () => {
    try {
      // Anonymous fast-path: no stored session token means /auth/me will
      // 401. We still call /api/usage (which returns 200 for anon via
      // the X-Device-Id header) so the Translate quota badge can show
      // the correct free-tier limits (e.g. '2 / 2 LEFT TODAY') instead
      // of a useless em-dash placeholder for first-time visitors.
      if (!getStoredSessionToken()) {
        setUser(null);
        try {
          const usageRes = await api.get("/usage");
          setUsage(usageRes?.data || null);
        } catch {
          // Network blip — leave usage as null; badge falls back to '—'
          // and the next refresh() retry will hydrate.
          setUsage(null);
        }
        return;
      }
      const [me, u] = await Promise.allSettled([
        api.get("/auth/me"),
        api.get("/usage"),
      ]);
      const meData = me.status === "fulfilled" ? me.value.data : null;
      // Treat a "ghost" user (token valid but name AND email both empty) as
      // unauthenticated — otherwise the UI shows a broken-looking avatar with
      // no way for the user to do anything about it. Forces re-sign-in.
      if (meData && !(meData.name || "").trim() && !(meData.email || "").trim()) {
        setStoredSessionToken(null);
        setUser(null);
      } else {
        setUser(meData);
        // Hydrate the per-account TTS voice preference. If the server has
        // a saved value, mirror it to localStorage so two users sharing
        // the same phone each keep their own translation voice across
        // sign-out/sign-in cycles. If the server has nothing yet, upload
        // the local pick on first sign-in so subsequent devices inherit
        // it.
        try {
          const serverPref = meData?.tts_voice_pref;
          if (serverPref === "female" || serverPref === "male") {
            if (getTtsVoice() !== serverPref) setTtsVoice(serverPref);
          } else if (meData?.user_id) {
            const localPref = getTtsVoice();
            api.put("/auth/voice-pref", { tts_voice_pref: localPref }).catch(() => {
              /* non-fatal — voice still works locally */
            });
          }
        } catch {
          /* localStorage unavailable — voice still works in-session */
        }
      }
      const usageData = u.status === "fulfilled" ? u.value.data : null;
      // Soft warning: when remaining of any kind crosses down to <=2, toast once.
      // Skip for users with unlimited entitlements (pro AND active trial). The
      // limit numbers shown in the dropdown (e.g. 40/40 menu) reflect the trial
      // ceiling not a free-plan ceiling, so warning would be misleading.
      if (usageData && usageData.plan !== "pro" && usageData.plan !== "trial" && usageData.limits) {
        Object.keys(usageData.limits).forEach((k) => {
          const limit = usageData.limits[k] || 0;
          const used = usageData.used?.[k] ?? 0;
          const remaining = Math.max(0, limit - used);
          const prev = prevRemainingRef.current[k];
          if (typeof prev === "number" && prev > 2 && remaining <= 2 && remaining > 0) {
            toast.message(
              `Only ${remaining} free ${KIND_LABEL[k] || k} remaining today`,
              { duration: 4500 }
            );
          }
          prevRemainingRef.current[k] = remaining;
        });
      }
      setUsage(usageData);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /auth/me check while OAuth callback fragment is present — let AuthCallback handle it.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
    // T49 — Probe the deployed backend at app launch so the
    // Diagnostics screen can immediately report which routes the
    // bundled REACT_APP_BACKEND_URL serves. Fire-and-forget — never
    // blocks UI. Result is cached and re-readable via getCanarySnapshot().
    import("./canary")
      .then(({ refreshCanary }) => refreshCanary())
      .catch(() => { /* non-fatal — diagnostics will show stale snapshot */ });
    // Configure RevenueCat on iOS so subsequent purchases work.
    // Safe to call repeatedly; configureIAP() is idempotent.
    if (isIOS()) {
      configureIAP(null).catch((e) =>
        console.warn("[IAP] configure failed:", e?.message || e)
      );
    }
  }, [refresh]);

  // Identify the user with RevenueCat once they're signed in.
  useEffect(() => {
    if (!isIOS() || !user?.user_id) return;
    identifyIAP(user.user_id).catch((e) =>
      console.warn("[IAP] identify failed:", e?.message || e)
    );
  }, [user?.user_id]);

  // T47 — Auto-sync the device's Apple StoreKit entitlement to the
  // backend whenever the user signs in OR the app comes to the
  // foreground. This is the silent path that flips Settings from
  // "Free plan" to "Pro" without the user ever tapping Restore. Uses
  // `Transaction.currentEntitlements` (no Apple ID password prompt).
  // Skips on web (RC path handles that) and when nobody is signed in.
  useEffect(() => {
    if (!isIOS() || !user?.user_id) return;
    let cancelled = false;
    let appSub;

    const runSync = async () => {
      try {
        const { syncAppleEntitlement } = await import("./iapAutoSync");
        const synced = await syncAppleEntitlement();
        if (!cancelled && synced?.active) {
          await refresh();
        }
      } catch (_e) { /* non-fatal — toast would be wrong UX */ }
    };

    // 1) Run immediately on mount / when a user signs in.
    runSync();

    // 2) Re-run every time the app comes back to the foreground.
    //    Covers: receipt refresh after purchase outside the app,
    //    family-share grant, subscription restored via App Store
    //    settings, etc.
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        appSub = await App.addListener("appStateChange", (state) => {
          if (state.isActive) runSync();
        });
      } catch (_e) { /* capacitor not available on web */ }
    })();

    return () => {
      cancelled = true;
      if (appSub?.remove) appSub.remove();
    };
  }, [user?.user_id, refresh]);

  // Push TTS voice-preference changes to the server while signed in so
  // the choice follows the account, not the device. Two travelers sharing
  // one phone (Pro tip: a couple) each keep their preferred translation
  // voice when they switch Google accounts.
  useEffect(() => {
    if (!user?.user_id) return;
    const unsub = subscribeTtsVoiceChange((newValue) => {
      api.put("/auth/voice-pref", { tts_voice_pref: newValue }).catch(() => {
        /* non-fatal — local pick is still in effect */
      });
    });
    return unsub;
  }, [user?.user_id]);

  // -------------------------------------------------------------
  // iOS deep-link handler — completes Google OAuth.
  // -------------------------------------------------------------
  // The native app is launched (or foregrounded) via `tiplingo://auth?session_id=XXX`
  // after the user taps "Open TipLingo →" on the public bridge page in
  // SFSafariViewController. We register this listener as early as possible
  // so the URL can never be missed, and we POST `/auth/session` directly so
  // the result is independent of which screen is currently rendered (e.g.
  // an open Paywall sheet).
  useEffect(() => {
    if (!isIOS() || _deepLinkListenerInstalled) return;
    _deepLinkListenerInstalled = true;

    let appSub;
    let browserMod;

    const completeAuth = async (sessionId) => {
      try {
        setStoredSessionToken(null);
        // IMPORTANT — disable withCredentials for this POST. The preview
        // URL is fronted by Cloudflare which force-adds wildcard CORS, and
        // that combined with credentials trips WKWebView's pre-flight
        // rejection ("Network Error"). The response body contains
        // `session_token` which the api response interceptor captures into
        // localStorage for subsequent Bearer auth — cookies aren't needed.
        const r = await api.post(
          "/auth/session",
          { session_id: sessionId },
          { withCredentials: false }
        );
        await refresh();
        const name = r?.data?.name || "";
        toast.success(name ? `Welcome, ${name}!` : "Signed in!");
        try {
          window.dispatchEvent(new Event("tiplingo:close-paywall"));
        } catch {
          /* non-fatal */
        }
      } catch (e) {
        // T49.W (Build 82) — Google auth failed. Match the rich diagnostic
        // /auth/apple ships so TestFlight users can screenshot the exact
        // failing URL + status + bundled backend host without needing
        // Console.app tethering. Previously this branch collapsed axios
        // "Network Error" into a single unactionable line — users couldn't
        // tell if the archive was calling preview, an old host, or if a
        // CORS/TLS issue was blocking the call.
        const detail = e?.response?.data?.detail;
        const code = e?.response?.status;
        const axiosCode = e?.code || "";
        const baseURL = e?.config?.baseURL || api?.defaults?.baseURL || "<no baseURL>";
        const path = e?.config?.url || "/auth/session";
        const method = (e?.config?.method || "post").toUpperCase();
        const fullUrl = baseURL.replace(/\/$/, "") + (path.startsWith("/") ? path : "/" + path);
        const bundledEnv = process.env.REACT_APP_BACKEND_URL || "<env missing>";
        const bodyPreview = typeof e?.response?.data === "string"
          ? e.response.data.slice(0, 160)
          : "";
        const diag = [
          `[/auth/session] ${method} ${fullUrl}`,
          `HTTP ${code ?? "(no response)"}${axiosCode ? " · " + axiosCode : ""}`,
          typeof detail === "string" && detail
            ? `detail: ${detail}`
            : (bodyPreview ? `body: ${bodyPreview}` : (e?.message ? `err: ${e.message}` : "")),
          `bundled REACT_APP_BACKEND_URL=${bundledEnv}`,
        ].filter(Boolean).join("\n");
        console.error("[auth] T49.W /auth/session failed:", diag, e);
        toast.error(diag, {
          duration: 15000,
          style: { whiteSpace: "pre-line", maxWidth: "92vw" },
        });
      }
    };

    const handleUrl = (url) => {
      if (!url) return;
      // Accept both `?session_id=…` and legacy `#session_id=…`. URL-decode
      // because the bridge `encodeURIComponent`s the value before handing
      // it to iOS.
      const m = String(url).match(/session_id=([^&#?]+)/);
      if (!m) return;
      let sid;
      try {
        sid = decodeURIComponent(m[1]);
      } catch {
        sid = m[1];
      }
      // Close in-app browser if still open.
      if (browserMod?.Browser?.close) {
        browserMod.Browser.close().catch(() => {});
      }
      completeAuth(sid);
    };

    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        browserMod = await import("@capacitor/browser");
        appSub = await App.addListener("appUrlOpen", (event) =>
          handleUrl(event?.url)
        );
        // Cold-start: drain the URL that launched the app, if any.
        try {
          const launch = await App.getLaunchUrl();
          if (launch?.url) handleUrl(launch.url);
        } catch {
          /* non-fatal */
        }
      } catch (e) {
        console.warn("[deep-link] listener setup failed:", e?.message || e);
        _deepLinkListenerInstalled = false; // allow retry on next mount
      }
    })();

    return () => {
      if (appSub?.remove) appSub.remove();
      _deepLinkListenerInstalled = false;
    };
  }, [refresh, openPaywall]);

  async function signInWithApple() {
    // Native Sign in with Apple via @capawesome/capacitor-apple-sign-in
    // (Capacitor 8 compatible). Required by App Store Review Guideline 4.8
    // — must be a co-equal, top-level login option alongside Google.
    // Apple sends name/email ONLY on the FIRST sign-in for any given Apple
    // ID; on subsequent sign-ins those fields are empty and the backend
    // relies on the stable `sub` claim from the identity token.
    if (!isIOS()) {
      // Web fallback: redirect to Apple's OAuth flow would go here. For
      // now we only ship native — web users use Google.
      toast.error("Sign in with Apple is available on iOS only");
      return;
    }
    const { AppleSignIn, SignInScope } = await import("@capawesome/capacitor-apple-sign-in");
    const nonce = (
      (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
      `${Date.now()}-${Math.random()}`
    ).replace(/-/g, "");
    let result;
    try {
      // T22 / Build 60+ — Capacitor 8 plugin from @capawesome.
      result = await AppleSignIn.signIn({
        scopes: [SignInScope.Email, SignInScope.FullName],
        nonce,
        // state is iOS-ignored but accepted; useful when this flow is
        // later wired up on web/Android.
        state: nonce,
      });
    } catch (e) {
      // User canceled the native sheet — don't toast an error.
      if (e?.code === "SIGN_IN_CANCELED" || /cancel/i.test(e?.message || "")) return;
      throw e;
    }
    if (!result?.idToken) {
      throw new Error("Apple sign-in returned no identity token");
    }

    // T46 — Wrap the backend exchange in a diagnostic-rich try block.
    // Build 67 surfaced a generic "Request failed with status code 404"
    // toast with no actionable detail. We can't reproduce a 404 against
    // the preview backend (the route exists and returns 401/422 for
    // various inputs), which strongly implies the Mac archive was
    // built against a different REACT_APP_BACKEND_URL or used a stale
    // `frontend/build/` from before the route was deployed. The
    // catch-and-rethrow below attaches the resolved URL, base URL, and
    // response payload so the next build's toast shows the exact
    // failing endpoint instead of axios's default error message.
    // T48 — Belt-and-suspenders against an edge cache or stale bundle.
    // Adding `?_t=<timestamp>` guarantees Cloudflare cannot serve a
    // cached 404 keyed by the bare URL; FastAPI ignores unknown query
    // params so the backend is unaffected. Also force `Cache-Control:
    // no-store` for the same reason on intermediate proxies.
    // T49.F (Build 73) — Per user directive, do NOT retry 404 to
    // hide a failure. A 404 from /auth/apple means the host or
    // gateway is wrong/stale; retrying just delays the inevitable
    // and hides the actionable diagnostic. The error is surfaced
    // directly with the rich diagnostic so the user / App Review
    // escalator can see exactly which URL the bundle is calling.
    let res;
    try {
      res = await api.post(
        `/auth/apple?_t=${Date.now()}`,
        {
          identity_token: result.idToken,
          nonce,
          email: result.email || null,
          full_name:
            result.givenName || result.familyName
              ? { givenName: result.givenName, familyName: result.familyName }
              : null,
        },
        { headers: { "Cache-Control": "no-store" } },
      );
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const baseURL = e?.config?.baseURL || api?.defaults?.baseURL || "<no baseURL>";
      const path = e?.config?.url || "/auth/apple";
      const method = (e?.config?.method || "post").toUpperCase();
      // axios resolves a relative path against the configured baseURL
      // — print both halves so a leading slash, missing /api prefix,
      // or wrong host is obvious from the toast alone. T48 also prints
      // the bundled REACT_APP_BACKEND_URL as a SEPARATE line so the
      // user can confirm at a glance whether the .ipa was built
      // against a stale env (independent of axios's baseURL config).
      const fullUrl = baseURL.replace(/\/$/, "") + (path.startsWith("/") ? path : "/" + path);
      const bundledEnv = process.env.REACT_APP_BACKEND_URL || "<env missing>";
      const diag = [
        `[/auth/apple] ${method} ${fullUrl}`,
        `HTTP ${status ?? "(no response)"}${detail ? " — " + (typeof detail === "string" ? detail : JSON.stringify(detail)) : ""}`,
        `bundled REACT_APP_BACKEND_URL=${bundledEnv}`,
      ].join("\n");
      console.error("[auth] T46/T48/T49 Apple sign-in exchange failed:", diag, e);
      const decorated = new Error(diag);
      decorated.code = `SIWA_HTTP_${status || "ERR"}`;
      decorated.response = e?.response;
      decorated.config = e?.config;
      throw decorated;
    }

    const token = res?.data?.session_token;
    if (token) setStoredSessionToken(token);
    await refresh();
    // T47 — Silent Apple StoreKit entitlement auto-sync. If this Apple
    // ID has an active TipLingo Pro subscription, the backend flips
    // plan→pro on its own and a second refresh() picks it up. No
    // Apple ID password prompt (uses Transaction.currentEntitlements,
    // not AppStore.sync). Failure is non-fatal — sign-in still succeeds.
    try {
      const { syncAppleEntitlement } = await import("./iapAutoSync");
      const synced = await syncAppleEntitlement();
      if (synced?.active) {
        await refresh();
      }
    } catch (_e) { /* never block sign-in on auto-sync */ }
    return res.data;
  }

  function signIn() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // On iOS (Capacitor) `window.location.origin` is `capacitor://localhost` which
    // Safari cannot redirect back to. We must therefore route the OAuth callback
    // through a public HTTPS bridge that re-opens the native app via a custom
    // URL scheme (`tiplingo://auth`). See IOSAuthBridge.jsx + the appUrlOpen
    // listener in App.js for the full flow.
    if (isIOS()) {
      // The web URL the iOS app was built against. Same React app deploys to
      // this origin, so the `/ios-auth-bridge` route is guaranteed to exist.
      const webOrigin = process.env.REACT_APP_BACKEND_URL;
      const redirectUrl = `${webOrigin}/ios-auth-bridge`;
      const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      // Open in SFSafariViewController so Apple Sign-In/Google show full chrome.
      import("@capacitor/browser").then(({ Browser }) => {
        Browser.open({ url: authUrl, presentationStyle: "popover" }).catch((e) =>
          console.warn("[auth] Browser.open failed:", e?.message || e)
        );
      });
      return;
    }
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  }

  async function signOut() {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("auth logout failed:", e?.message || e);
    }
    setStoredSessionToken(null);
    setUser(null);
    if (isIOS()) {
      resetIAP().catch(() => {});
    }
    // T49 — Clear locally-cached StoreKit entitlement so the next
    // signed-in account doesn't inherit the previous user's Pro
    // status. The cache is repopulated on the next sign-in's silent
    // sync if the new Apple ID actually has an entitlement.
    try {
      const { clearCachedEntitlement } = await import("./entitlementCache");
      clearCachedEntitlement();
    } catch { /* non-fatal */ }
    await refresh();
  }

  async function startTrial() {
    try {
      const r = await api.post("/auth/start-trial");
      setUser(r.data);
      // Optimistically widen the local usage limits to match TRIAL_LIMITS
      // (kept in sync with backend) so the UI reflects the new entitlement
      // immediately, even if the /usage refresh below is delayed or fails.
      setUsage((prev) => ({
        ...(prev || { used: {} }),
        plan: "trial",
        limits: {
          translate_text: 200,
          translate_voice: 60,
          receipt: 40,
          menu: 40,
        },
      }));
      await refresh();
      return r.data;
    } catch (e) {
      if (e?.response?.status === 401) {
        signIn();
      }
      throw e;
    }
  }

  // T49 — Resilient Pro detection. Backend `user.plan === 'pro'` is
  // the long-running source of truth, but during transient backend
  // outages (or while the silent T47 sync hasn't landed yet) a paid
  // Apple subscriber would otherwise see "Free plan" in Settings.
  // We OR-in the locally-cached StoreKit entitlement (Apple-signed,
  // expirationDate in the future, no revocationDate). The cache is
  // populated only by validated transactions from
  // `Transaction.currentEntitlements`, so this can't be forged.
  const [isProLocal, setIsProLocal] = useState(false);
  useEffect(() => {
    let alive = true;
    let tick;
    (async () => {
      try {
        const { isCachedEntitlementActive } = await import("./entitlementCache");
        const check = () => {
          if (!alive) return;
          setIsProLocal(isCachedEntitlementActive());
        };
        check();
        // Re-check every 60s in case the cached expiration lapses
        // mid-session — UI naturally degrades to "Free plan".
        tick = setInterval(check, 60_000);
      } catch { /* non-fatal */ }
    })();
    return () => { alive = false; if (tick) clearInterval(tick); };
  }, [user?.user_id, usage?.plan]);

  const isProEffective = user?.plan === "pro" || usage?.plan === "pro" || isProLocal;

  const value = useMemo(
    () => ({ user, usage, loading, signIn, signInWithApple, signOut, refresh, startTrial, openPaywall, isProEffective }),
    // signIn/signInWithApple/signOut/startTrial use stable refs; refresh is the only one with a hook id
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, usage, loading, refresh, openPaywall, isProEffective]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
